import { useState } from 'react';
import { getPaymentReceipt } from '../api/payments.api';
import { Simulate } from 'react-dom/test-utils';
import waiting = Simulate.waiting;

const ESC = '\x1B'; // ESC 명령어
const LF = '\x0A'; // 줄바꿈

export interface PrintParams {
  paymentRecordCode: number;
  orderName: string;
  memberName: string;
  paymentTargetCode: string;
  cardType: string;
  cardNum: string;
  installment: string;
  approveNum: string;
  paymentDttm: string;
  paymentAmount: number;
  taxAmount: number;
  campusName: string;
  ceoName: string;
  corporateRegistrationNo: string;
  telephoneNo: string;
  address: string;
}

const getByteLength = (str: string): number => {
  let l = 0;
  for (let i = 0; i < str.length; i++) {
    const c = escape(str.charAt(i));
    if (c.length === 1) l++;
    else if (c.indexOf('%u') !== -1) l += 3;
    else if (c.indexOf('%') !== -1) l += c.length / 3;
  }
  return l;
};

const padNumber = (value: string | number, width: number): string => {
  const str = value.toString();
  return str.length >= width ? str : new Array(width - str.length + 1).join('0') + str;
};


const makeSendData = (type: string, sendData: string): string => {
  const bodyLen = getByteLength(sendData);
  const totalLen = 12 + bodyLen;
  return padNumber(totalLen, 4) + type + '    ' + padNumber(bodyLen, 4) + sendData;
};

const sendPostRequest = async (sendbuf: string): Promise<string> => {
  const daemonUrl = `http://localhost:9088`;
  const response = await fetch(daemonUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
    },
    body: sendbuf,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.text();
};

const formatLine = (label: string, value: string) => {
  const labelWidth = 10;
  const separatorPosition = 16;
  return label.padEnd(labelWidth, ' ') + ':'.padStart(separatorPosition - label.length, ' ') + ` ${value}`;
};

const formatDateFromString = (dateString?: string): string => {
  if (!dateString || dateString.length !== 12) return '';

  const year = `20${dateString.substring(0, 2)}`; // "25" → "2025"
  const month = dateString.substring(2, 4); // "02"
  const day = dateString.substring(4, 6); // "27"
  const hour = dateString.substring(6, 8); // "10"
  const minute = dateString.substring(8, 10); // "46"
  const second = dateString.substring(10, 12); // "45"

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const formatNumber = (value?: number | string): string => {
  if (!value) return '0';
  return new Intl.NumberFormat('ko-KR').format(Number(value)); // 한국 숫자 포맷 적용
};


const useReceiptPrinter = () => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState<string>();

  const printReceipt = async (orderId: number) => {
    setIsPrinting(true);
    setError(undefined);

    const { data: res } = await getPaymentReceipt(orderId);
    const data = res?.data;

    try {
      let receipt = 'PRNT' + LF;
      receipt += ESC + '\x21' + '\x30'; // 확대 출력
      receipt += '신용카드 매출전표' + LF;
      receipt += ESC + '\x21' + '\x00'; // 확대 해제
      receipt += '================================================' + LF;

      receipt += formatLine('주문번호', data?.paymentRecordCode.toString() || 'N/A') + LF;
      receipt += formatLine('구매상품', data?.orderName || 'N/A') + LF;
      receipt += formatLine('승인번호', data?.approveNum || 'N/A') + LF;
      receipt += formatLine('결제일시', formatDateFromString(data?.paymentDttm)) + LF;
      receipt += formatLine('결제금액', `${formatNumber(data?.paymentAmount)} 원`) + LF;
      receipt += '================================================' + LF;

      receipt += formatLine('카드종류', data?.cardType || 'N/A') + LF;
      receipt += formatLine('카드번호', data?.cardNum || '****-****') + LF;
      receipt += formatLine('할부', `${(data?.installment === '00') || !data?.installment ? '일시불' : `${parseInt(data.installment, 10)}개월`}`) + LF;
      receipt += formatLine('결제상태', '완료') + LF;
      receipt += '================================================' + LF;

      receipt += formatLine('공급가액', `${formatNumber((data?.paymentAmount || 0) - (data?.taxAmount || 0))} 원`) + LF;
      receipt += formatLine('부가세', `${formatNumber(data?.taxAmount)} 원`) + LF;
      receipt += formatLine('합계', `${formatNumber(data?.paymentAmount)} 원`) + LF;
      receipt += '================================================' + LF;

      receipt += '이용상점' + LF;
      receipt += `${data?.campusName?.trim() || 'N/A'}` + '|';
      receipt += `대표자명: ${data?.ceoName?.trim() || 'N/A'}` + '|';
      receipt += `사업자등록자번호: ${data?.corporateRegistrationNo?.trim() || 'N/A'}` + '|';
      receipt += `전화: ${data?.telephoneNo?.trim() || 'N/A'}` + '|';
      receipt += `주소: ${data?.address?.trim() || 'N/A'}` + LF;
      receipt += LF;

      receipt += '결제서비스업체' + LF;
      receipt += '나이스페이먼츠 주식회사' + '|';
      receipt += `대표자명: 김광철` + '|';
      receipt += `사업자등록자번호: 815-81-00527` + '|';
      receipt += `전화: 1661-7335` + '|';
      receipt += `주소: 서울특별시 영등포구 문래로 28길 25, 세미콜론문래 N타워 9층` + LF;
      receipt += '================================================' + LF;

      receipt += '- 부가가치세법 제 46조 3항에 따라 신용카드 매출전표도 매입세금계산서로 사용할 수 있습니다.' + LF;
      receipt += '- 과세제외액(컵 보증금 등)은 조세특례제한법 제 126조의2에 따른 신용카드 등 사용금액에 대한 소득공제에서 제외딥니다' + LF;

      receipt += ESC + 'a' + '1'; // 중앙 정렬
      receipt += ESC + 'i' + LF + LF; // 용지 컷팅
      receipt += '\x1B' + 'd' + '\x01';

      console.log('[Receipt Print] ', receipt);

      const formattedBuf = makeSendData('PCAT', receipt);
      const encodedBuf = encodeURIComponent(formattedBuf);
      const res = await sendPostRequest(encodedBuf);

      console.log(res);

      await new Promise(resolve => setTimeout(resolve, 5000));
    } catch (err) {
      setError(err instanceof Error ? err.message : '프린트 실패');
      setIsPrinting(false);
    } finally {
      setIsPrinting(false);
    }
  };

  return { printReceipt, isPrinting, error };
};

export default useReceiptPrinter;
